'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  inquiryType: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  fleetSize: string;
  message: string;
}

interface InquiryType {
  value: string;
  label: string;
  icon: string;
}

const ContactForm = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    inquiryType: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    fleetSize: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const inquiryTypes: InquiryType[] = [
    { value: 'fleet-valuation', label: 'Fleet Valuation Request', icon: 'CalculatorIcon' },
    { value: 'urgent-liquidation', label: 'Urgent Fleet Liquidation', icon: 'BoltIcon' },
    { value: 'consultation', label: 'Schedule Consultation', icon: 'CalendarIcon' },
    { value: 'general', label: 'General Inquiry', icon: 'ChatBubbleLeftRightIcon' }
  ];

  const fleetSizes = [
    '1-5 vehicles',
    '6-15 vehicles',
    '16-50 vehicles',
    '51-100 vehicles',
    '100+ vehicles'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setSubmitStatus('success');
    
    // Reset form after success
    setTimeout(() => {
      setFormData({
        inquiryType: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        fleetSize: '',
        message: ''
      });
      setSubmitStatus('idle');
    }, 3000);
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-brand p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-10 bg-muted rounded"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-10 bg-muted rounded"></div>
            <div className="h-10 bg-muted rounded"></div>
          </div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-brand p-6 md:p-8">
      <div className="mb-6">
        <h2 className="text-2xl md:text-3xl font-bold font-headline text-foreground mb-2">
          Send Us a Message
        </h2>
        <p className="text-muted-foreground">
          Fill out the form below and we'll get back to you within 2 business hours
        </p>
      </div>

      {submitStatus === 'success' && (
        <div className="mb-6 p-4 bg-success/10 border border-success rounded-lg flex items-start space-x-3">
          <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-success flex-shrink-0" />
          <div>
            <p className="font-semibold text-success">Message Sent Successfully!</p>
            <p className="text-sm text-success/80">We'll contact you within 2 business hours.</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Inquiry Type */}
        <div>
          <label htmlFor="inquiryType" className="block text-sm font-semibold text-foreground mb-2">
            Inquiry Type *
          </label>
          <select
            id="inquiryType"
            name="inquiryType"
            value={formData.inquiryType}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
          >
            <option value="">Select inquiry type</option>
            {inquiryTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-semibold text-foreground mb-2">
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
              placeholder="John"
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-semibold text-foreground mb-2">
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
              placeholder="Smith"
            />
          </div>
        </div>

        {/* Contact Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
              placeholder="john.smith@company.com"
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
              placeholder="(555) 123-4567"
            />
          </div>
        </div>

        {/* Company & Fleet Size */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="company" className="block text-sm font-semibold text-foreground mb-2">
              Company Name *
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
              placeholder="ABC Logistics Inc."
            />
          </div>
          <div>
            <label htmlFor="fleetSize" className="block text-sm font-semibold text-foreground mb-2">
              Fleet Size *
            </label>
            <select
              id="fleetSize"
              name="fleetSize"
              value={formData.fleetSize}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
            >
              <option value="">Select fleet size</option>
              {fleetSizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-foreground mb-2">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={6}
            className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 resize-none"
            placeholder="Tell us about your fleet and how we can help..."
          ></textarea>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full md:w-auto px-8 py-4 bg-cta text-cta-foreground font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-200 ease-out hover:-translate-y-0.5 shadow-brand disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
        >
          {isSubmitting ? (
            <>
              <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
              <span>Sending...</span>
            </>
          ) : (
            <>
              <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
              <span>Send Message</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;